import { useState } from "react";
import {
  OrgAvatar,
  LinkedPill,
  PendingPill,
  OverflowButton,
  Popover,
  PopoverItem,
  MenuDivider,
  ResendIcon,
  ChevronRightIcon,
  GlobeIcon,
  CloseIcon,
  UserIcon,
  PlusIcon,
  LayersIcon,
  Button,
  EmptyState,
  SkeletonBlock,
  CardGrid,
} from "./v2";

// Partners wall — My Agency V2 rebuild, 1:1 from ARTBOARD-SPEC board 4a
// (+ 2i empty state, 4c pending-partner menu). ONE showcase wall, two
// partner types rendered as one visual family:
//   - Linked (platform agency): mutual consent; "Linked" pill; links to
//     their public profile. Pending invites are visible only to the
//     sender's admins and NEVER on the public wall.
//   - Showcase (external org): manual, logo + name + optional type +
//     website. No consent flow.
// PRESENTATION ONLY (Brief Rules 6/8): the grouping/normalization logic
// and every handler prop are byte-identical to the previous version —
// only markup/styling changed. Partners is a pure showcase (t4): no
// stats, no statuses, no contact rows.
//
// Accepts the flat wall list {type:'linked'|'showcase', state, ...} (raw
// array or {results:[]}) or the legacy untyped array (treated as linked)
// for graceful degradation.

// Wall card shell (board 4a): padding 26/18/20, centered column, gap 4.
const cardBase =
  "relative bg-white border border-[#E3E9F1] rounded-xl shadow-[0_1px_2px_rgba(15,23,42,.05)] px-[18px] pt-[26px] pb-5 flex flex-col items-center gap-1 min-w-0";

// Org name line: 14.5/600 centered ellipsis, margin-top 10.
const nameBase =
  "text-[14.5px] font-semibold mt-2.5 text-center max-w-full whitespace-nowrap overflow-hidden text-ellipsis";

const LinkedCard = ({ partner, onClick, onMenu }) => (
  <div
    className={`${cardBase} cursor-pointer hover:border-[#B9D8FF] hover:shadow-[0_4px_14px_rgba(15,23,42,.08)]`}
    onClick={() => onClick?.(partner.id)}
  >
    {onMenu && (
      <OverflowButton
        variant="wallOverflow"
        iconSize={15}
        aria-label="Partner actions"
        onClick={(e) => {
          e.stopPropagation();
          onMenu(partner);
        }}
      />
    )}
    <OrgAvatar src={partner.logo_url} name={partner.name} seed={partner.id ?? partner.name} size={64} radius={16} />
    <span className={`${nameBase} text-[#0F172A]`}>{partner.name}</span>
    <LinkedPill>Linked</LinkedPill>
    <span className="inline-flex items-center gap-1 text-[12px] font-medium text-[#1668D9] mt-[3px]">
      View profile
      <ChevronRightIcon size={11} stroke="#1668D9" />
    </span>
  </div>
);

const ShowcaseCard = ({ partner, onMenu }) => (
  <div className={cardBase}>
    {onMenu && (
      <OverflowButton
        variant="wallOverflow"
        iconSize={15}
        aria-label="Partner actions"
        onClick={(e) => {
          e.stopPropagation();
          onMenu(partner);
        }}
      />
    )}
    <OrgAvatar src={partner.logo_url} name={partner.name} seed={partner.id ?? partner.name} size={64} radius={16} />
    <span className={`${nameBase} text-[#0F172A]`}>{partner.name}</span>
    {/* Type line (wall): plain 12px #94A3B8; missing type renders "—" in
        #C4CEDA (CMP-PILL partner-type rule, GLY-10 em dash). */}
    {partner.type_label ? (
      <span className="text-[12px] text-[#94A3B8]">{partner.type_label}</span>
    ) : (
      <span className="text-[12px] text-[#C4CEDA]">—</span>
    )}
    {partner.website && (
      <a
        href={/^https?:\/\//.test(partner.website) ? partner.website : `https://${partner.website}`}
        target="_blank"
        rel="noreferrer noopener"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#1668D9] mt-[3px]"
      >
        <GlobeIcon size={11} />
        {partner.website.replace(/^https?:\/\//, "")}
      </a>
    )}
  </div>
);

// Pending invite card (board 4a "Balkan XI Agency"): dashed 1.5px border,
// no shadow, not clickable; muted avatar at opacity .75; name #64748B;
// amber Pending pill; server-provided sent label. Overflow opens the
// pending-partner menu (board 4c): View their profile · Resend invite
// (SYM-26; P-1, sender-side pending only — this card IS sender-side, the
// wall serves pending rows only to the sender's admins) · Revoke invite.
// Resend keeps the menu open with a per-invite busy label while the
// request is in flight, then closes; status stays pending (no list change).
const PendingCard = ({
  partner,
  menuOpen,
  onMenuToggle,
  onMenuClose,
  onViewProfile,
  onResend,
  resending,
  onRevoke,
}) => (
  <div className="relative bg-white border-[1.5px] border-dashed border-[#C4CEDA] rounded-xl px-[18px] pt-[26px] pb-5 flex flex-col items-center gap-1 min-w-0">
    {onRevoke && (
      <OverflowButton
        variant="wallOverflow"
        iconSize={15}
        open={menuOpen}
        aria-label="Partner actions"
        onClick={(e) => {
          e.stopPropagation();
          onMenuToggle(partner);
        }}
      />
    )}
    {menuOpen && (
      <Popover onClose={onMenuClose} style={{ position: "absolute", right: 8, top: 40 }}>
        {onViewProfile && (
          <PopoverItem
            icon={<UserIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onMenuClose();
              onViewProfile(partner.id);
            }}
          >
            View their profile
          </PopoverItem>
        )}
        {onResend && (
          <PopoverItem
            icon={<ResendIcon />}
            disabled={resending}
            onClick={async (e) => {
              e.stopPropagation();
              if (resending) return;
              await onResend(partner);
              onMenuClose();
            }}
          >
            {resending ? "Resending…" : "Resend invite"}
          </PopoverItem>
        )}
        {(onViewProfile || onResend) && <MenuDivider />}
        <PopoverItem
          destructive
          icon={<CloseIcon size={14} strokeWidth={2} stroke="#DC2626" />}
          onClick={(e) => {
            e.stopPropagation();
            onMenuClose();
            onRevoke(partner);
          }}
        >
          Revoke invite
        </PopoverItem>
      </Popover>
    )}
    <OrgAvatar
      src={partner.logo_url}
      name={partner.name}
      pending
      size={64}
      radius={16}
      style={{ opacity: 0.75 }}
    />
    <span className={`${nameBase} text-[#64748B]`}>{partner.name}</span>
    <PendingPill>Pending</PendingPill>
    {partner.sent_label && <span className="text-[11.5px] text-[#94A3B8] mt-[3px]">{partner.sent_label}</span>}
  </div>
);

// Loading skeleton — wall-card anatomy (org avatar tile → name → pill),
// token colors + tpPulse (CMP-SKELETON; no dedicated partners board).
const WallSkeletonCard = () => (
  <div className="bg-white border border-[#E3E9F1] rounded-xl px-[18px] pt-[26px] pb-5 flex flex-col items-center gap-1 min-w-0">
    <SkeletonBlock width={64} height={64} radius={16} />
    <SkeletonBlock width="60%" height={12} style={{ marginTop: 10 }} />
    <SkeletonBlock width={64} height={18} radius={999} tone="light" style={{ marginTop: 4 }} />
  </div>
);

const PartnersGrid = ({
  partners,
  partnersLoading,
  partnersError,
  onPartnerClick,
  onAddPartner,
  onLinkedMenu,
  onShowcaseMenu,
  onPendingMenu,
  onResendInvite,
  resendingPartnerId = null,
  isCurrentUserAdmin = false,
}) => {
  // Pending-menu open state (presentation chrome only).
  const [openPendingMenuId, setOpenPendingMenuId] = useState(null);

  // The wall is a flat list: {type:"linked",state:"accepted"|"pending",...} |
  // {type:"showcase",state:"active",...}. Accept the raw array or {results:[]},
  // and fall back to treating untyped items as accepted linked partners.
  const items = Array.isArray(partners) ? partners : partners?.results || [];
  const norm = (i) => ({ ...i, id: i.partner_id ?? i.showcase_id ?? i.id });
  const linked = items
    .filter((i) => (i.type ? i.type === "linked" && i.state !== "pending" : true))
    .map(norm);
  const pending = items.filter((i) => i.type === "linked" && i.state === "pending").map(norm);
  const showcase = items.filter((i) => i.type === "showcase").map(norm);
  const total = linked.length + pending.length + showcase.length;

  if (partnersLoading) {
    return (
      <div className="px-6 pt-3.5 pb-6">
        <CardGrid min={215} gap={16} marginTop={0}>
          {[0, 1, 2, 3].map((i) => (
            <WallSkeletonCard key={i} />
          ))}
        </CardGrid>
      </div>
    );
  }

  if (partnersError) {
    return (
      <div className="px-6 pt-3.5 pb-6">
        <div className="text-center py-12 text-[13.5px] text-[#DC2626] bg-white border border-[#E3E9F1] rounded-xl">
          {partnersError}
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 pt-3.5 pb-6">
      {/* Wall caption (board 4a): 13px #64748B. The "Add partner" primary
          action lives in the tabs+action row (page shell, D-09). */}
      <p className="text-[13px] text-[#64748B]">
        Shown on your agency’s public profile. Linked agencies appear on both walls.
      </p>

      {total === 0 ? (
        /* Empty state (board 2i, still-current piece): 340px tile, icon
           56px r16 square-ish tile #EAF3FF + SYM-31, padding 44px 28px. */
        <div className="flex justify-center" style={{ marginTop: 14 }}>
          <EmptyState
            iconShape="tile"
            icon={<LayersIcon />}
            title="Showcase your partners"
            body="Law firms, academies, brands: the organisations that vouch for your agency, on your public profile."
            padding="44px 28px"
            style={{ width: 340, maxWidth: "100%" }}
            cta={
              isCurrentUserAdmin && onAddPartner ? (
                <Button icon={<PlusIcon />} onClick={onAddPartner}>
                  Add partner
                </Button>
              ) : null
            }
          />
        </div>
      ) : (
        <CardGrid min={215} gap={16} marginTop={14}>
          {linked.map((p) => (
            <LinkedCard
              key={`l-${p.id ?? p.agency_id}`}
              partner={p}
              onClick={onPartnerClick}
              onMenu={isCurrentUserAdmin ? onLinkedMenu : undefined}
            />
          ))}
          {showcase.map((p) => (
            <ShowcaseCard key={`s-${p.id}`} partner={p} onMenu={isCurrentUserAdmin ? onShowcaseMenu : undefined} />
          ))}
          {/* Pending = sender-only; never rendered on the public wall. */}
          {isCurrentUserAdmin &&
            pending.map((p) => (
              <PendingCard
                key={`p-${p.id ?? p.agency_id}`}
                partner={p}
                menuOpen={openPendingMenuId === (p.id ?? p.agency_id)}
                onMenuToggle={() =>
                  setOpenPendingMenuId((cur) => (cur === (p.id ?? p.agency_id) ? null : p.id ?? p.agency_id))
                }
                onMenuClose={() => setOpenPendingMenuId(null)}
                onViewProfile={onPartnerClick}
                onResend={onResendInvite}
                resending={resendingPartnerId === (p.id ?? p.agency_id)}
                onRevoke={onPendingMenu}
              />
            ))}
        </CardGrid>
      )}

      {isCurrentUserAdmin && pending.length > 0 && (
        <p className="text-[11.5px] text-[#94A3B8] mt-3.5">
          Pending invites are visible only to your agency, never on the public wall.
        </p>
      )}
    </div>
  );
};

export default PartnersGrid;
